import React, { useEffect, useRef, useState } from 'react';
import './SignaturePad.css';

const MAX_UPLOAD_BYTES = 1024 * 1024;

/**
 * Handtekening tekenen (muis/touch/pen) of uploaden. Geeft een PNG/JPEG data-URL terug via onChange,
 * of '' wanneer de handtekening gewist wordt.
 */
const SignaturePad = ({ label, value, onChange, height = 150 }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasStrokesRef = useRef(false);
  const [mode, setMode] = useState(value ? 'preview' : 'draw');

  useEffect(() => {
    if (!value && mode === 'preview') setMode('draw');
  }, [value, mode]);

  // Canvas scherp houden op hoge-resolutieschermen.
  useEffect(() => {
    if (mode !== 'draw') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    hasStrokesRef.current = false;
  }, [mode]);

  const pointFor = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const handlePointerDown = (event) => {
    event.preventDefault();
    canvasRef.current.setPointerCapture(event.pointerId);
    drawingRef.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = pointFor(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const handlePointerMove = (event) => {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = pointFor(event);
    ctx.lineTo(x, y);
    ctx.stroke();
    hasStrokesRef.current = true;
  };

  const finishStroke = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (hasStrokesRef.current) onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (canvas) canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    hasStrokesRef.current = false;
    onChange('');
    setMode('draw');
  };

  const handleUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!/^image\/(png|jpe?g|gif|webp)$/.test(file.type)) {
      window.alert('Upload een afbeelding (PNG, JPG, GIF of WEBP).');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      window.alert('Afbeelding is te groot (maximaal 1 MB).');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onChange(String(reader.result || ''));
      setMode('preview');
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="signature-pad">
      <div className="signature-pad-head">
        {label && <span className="signature-pad-label">{label}</span>}
        <div className="signature-pad-actions">
          <button type="button" className={`signature-pad-tab ${mode === 'draw' ? 'active' : ''}`} onClick={() => setMode('draw')}>Tekenen</button>
          <label className="signature-pad-tab">
            Uploaden
            <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleUpload} hidden />
          </label>
          <button type="button" className="signature-pad-tab danger" onClick={clear}>Wissen</button>
        </div>
      </div>

      {mode === 'preview' && value ? (
        <div className="signature-pad-preview" style={{ height }}>
          <img src={value} alt={`Handtekening ${label || ''}`} />
        </div>
      ) : (
        <canvas
          ref={canvasRef}
          className="signature-pad-canvas"
          style={{ height }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={finishStroke}
          onPointerLeave={finishStroke}
          onPointerCancel={finishStroke}
        />
      )}
      <small className="signature-pad-hint">
        {value ? 'Handtekening ingesteld.' : 'Teken met muis, vinger of pen, of upload een afbeelding.'}
      </small>
    </div>
  );
};

export default SignaturePad;
